import WearDisplayHelpers, {
  WEAR_DIRECTION_DECREASING,
} from './wearDisplayHelpers';

export interface ActionStatusValidationResult {
  isValid: boolean;
  /** Present when isValid is false — the toast message to show. */
  message?: string;
  severity?: 'alert' | 'warning';
}

export { WEAR_DIRECTION_DECREASING };

class ActionStatusHelpers {
  /**
   * Increasing / Unknown: wear% = (value − baseline) ÷ limit × 100 — the reading grows from the
   * baseline and `limit` is the allowed growth (a tolerance for e.g. pitch extension).
   * Decreasing (#10233): the reading shrinks from a full baseline toward an absolute condemning
   * limit, so wear% = (baseline − value) ÷ (baseline − limit) × 100. Until an SME classifies a
   * dimension its wearDirection is Unknown and the legacy Increasing formula applies unchanged.
   *
   * An invalid (≤ 0 or missing) divisor means NO percentage can be calculated — returns null
   * instead of substituting one (a previous fallback of `limit === 0 ? 1 : limit` fabricated
   * 800%-style values). Mirrors the reviewed semantics merged in PR #9929. Callers must treat
   * null as "no percentage".
   */
  static calculateWearPercentage({
    value,
    baselineValue,
    limit,
    wearDirection,
  }: {
    value: number;
    baselineValue: number;
    limit: number;
    wearDirection?: number;
  }): number | null {
    if (wearDirection === WEAR_DIRECTION_DECREASING) {
      const span = baselineValue - limit;
      if (!(span > 0)) return null;
      return ((baselineValue - value) / span) * 100;
    }
    if (!(limit > 0)) return null;
    return ((value - baselineValue) / limit) * 100;
  }

  /**
   * Pure validation + message selection for the action status thresholds
   * (#10205), extracted from ActionTypeStore.validateActionStatus so it can
   * be unit-tested without importing the store (whose module is mocked
   * globally by component tests, poisoning any test file that loads after
   * them). Validation rules are unchanged: block below baseline and above
   * 250%, warn above 150%.
   *
   * When the entered `value` is provided, rejection messages state the
   * acceptable range (in the entry mode's `unit`, default mm) instead of the
   * bare legacy threshold text.
   */
  static evaluateActionStatusValidation({
    percentage,
    minValue,
    maxValue,
    multiplier = 1,
    value,
    unit,
  }: {
    percentage: number;
    minValue: number;
    maxValue: number;
    multiplier?: number;
    value?: number;
    unit?: 'mm' | '%';
  }): ActionStatusValidationResult {
    const pct = Number(percentage || 0);
    const rangeMessage =
      value !== undefined
        ? WearDisplayHelpers.formatOutOfRangeError({
            value,
            minValue: (minValue ?? 0) * multiplier,
            maxValue: (maxValue ?? 100) * multiplier,
            unit,
          })
        : null;

    if (pct < 0) {
      return {
        isValid: false,
        severity: 'alert',
        message:
          rangeMessage ??
          `Value equal or below ${minValue ? minValue * multiplier : 0}`,
      };
    }
    if (pct > 250) {
      return {
        isValid: false,
        severity: 'alert',
        message:
          rangeMessage ??
          `Value above 250% of max ${maxValue ? maxValue * multiplier : 100}`,
      };
    }
    if (pct > 150) {
      return {
        isValid: false,
        severity: 'warning',
        message:
          rangeMessage ??
          `Value above 150% of max ${maxValue ? maxValue * multiplier : 100}`,
      };
    }

    return { isValid: true };
  }
}

export default ActionStatusHelpers;
